import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BASE_URL from "../../../config/config";

const EditUser = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        phone: '',
        fullName: '',
        password: '',
        role: 'user'
    });
    
    const [usernameError, setUsernameError] = useState('');
    const [emailError, setEmailError] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [isUsernameAvailable, setIsUsernameAvailable] = useState(true);
    const [isEmailAvailable, setIsEmailAvailable] = useState(true);
    const [isPhoneAvailable, setIsPhoneAvailable] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [originalUsername, setOriginalUsername] = useState('');
    const [originalEmail, setOriginalEmail] = useState('');
    const [originalPhone, setOriginalPhone] = useState('');
    const [csrfToken, setCsrfToken] = useState('');
    
    // Refs for debounce timers
    const usernameTimer = useRef(null);
    const emailTimer = useRef(null);
    const phoneTimer = useRef(null);
    
    // دریافت توکن CSRF
    useEffect(() => {
        const getCsrfToken = async () => {
            try {
                const response = await fetch(`${BASE_URL}/csrf-token`, {
                    method: 'GET',
                    credentials: 'include'
                });
                
                if (!response.ok) {
                    throw new Error('خطا در دریافت توکن CSRF');
                }
                
                const data = await response.json();
                setCsrfToken(data.csrfToken);
            } catch (error) {
                console.error('خطا در دریافت توکن CSRF:', error);
                setMessage('خطا در برقراری ارتباط با سرور');
            }
        };
        
        getCsrfToken();
    }, []);
    
    // تنظیم هدرهای درخواست
    const getAuthHeader = () => {
        const token = localStorage.getItem('token');
        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'CSRF-Token': csrfToken
        };
    };
    
    // بارگذاری اطلاعات کاربر
    useEffect(() => {
        const fetchUserData = async () => {
            if (!csrfToken) return;
            
            try {
                const response = await fetch(`${BASE_URL}/users/${id}`, {
                    method: 'GET',
                    headers: getAuthHeader(),
                    credentials: 'include'
                });
                
                if (!response.ok) {
                    if (response.status === 401 || response.status === 403) {
                        localStorage.removeItem('token');
                        navigate('/login');
                        throw new Error('دسترسی غیر مجاز');
                    }
                    throw new Error('خطا در بارگذاری اطلاعات کاربر');
                }
                
                const userData = await response.json();
                
                setOriginalUsername(userData.username);
                setOriginalEmail(userData.email);
                setOriginalPhone(userData.phone || '');
                
                setFormData({
                    username: userData.username,
                    email: userData.email,
                    phone: userData.phone || '',
                    fullName: userData.fullName || '',
                    password: '',
                    role: userData.role || 'user'
                });
            } catch (error) {
                setMessage(error.message);
            }
        };
        
        if (csrfToken) {
            fetchUserData();
        }
    }, [id, navigate, csrfToken]);
    
    // بررسی در دسترس بودن فیلدها
    const checkAvailability = async (field, value) => {
        if ((field === 'username' && value === originalUsername) ||
            (field === 'email' && value === originalEmail) ||
            (field === 'phone' && value === originalPhone)) {
            return;
        }
        
        if (!csrfToken) return;
        
        try {
            const response = await fetch(`${BASE_URL}/check-availability?${field}=${value}&userId=${id}`, {
                method: 'GET',
                headers: getAuthHeader(),
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error(`خطا در بررسی دسترسی ${field}`);
            }
            
            const data = await response.json();
            
            switch (field) {
                case 'username':
                    setIsUsernameAvailable(data.available);
                    if (!data.available) setUsernameError('این نام کاربری قبلاً استفاده شده است');
                    break;
                case 'email':
                    setIsEmailAvailable(data.available);
                    if (!data.available) setEmailError('این ایمیل قبلاً استفاده شده است');
                    break;
                case 'phone':
                    setIsPhoneAvailable(data.available);
                    if (!data.available) setPhoneError('این شماره تماس قبلاً استفاده شده است');
                    break;
                default:
                    break;
            }
        } catch (error) {
            console.error(`خطا در بررسی دسترسی ${field}:`, error);
        }
    };
    
    // اعتبارسنجی‌ها
    const validateUsername = (username) => {
        const usernameRegex = /^[a-zA-Z0-9]+$/;
        return usernameRegex.test(username) && username.length >= 3;
    };
    
    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };
    
    const validatePhone = (phone) => {
        if (!phone) return true;
        const phoneRegex = /^[0-9]{11}$/;
        return phoneRegex.test(phone);
    };
    
    const validatePassword = (password) => {
        if (!password) return true;
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
        return passwordRegex.test(password);
    };
    
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        
        // پاک کردن تایمرهای قبلی
        if (usernameTimer.current) clearTimeout(usernameTimer.current);
        if (emailTimer.current) clearTimeout(emailTimer.current);
        if (phoneTimer.current) clearTimeout(phoneTimer.current);
        
        switch (name) {
            case 'username':
                if (!validateUsername(value)) {
                    setUsernameError(value.length < 3 
                        ? 'نام کاربری باید حداقل 3 کاراکتر باشد'
                        : 'نام کاربری فقط می‌تواند شامل حروف انگلیسی و اعداد باشد');
                    setIsUsernameAvailable(false);
                } else {
                    setUsernameError('');
                    if (value !== originalUsername) {
                        usernameTimer.current = setTimeout(() => {
                            checkAvailability('username', value);
                        }, 500);
                    } else {
                        setIsUsernameAvailable(true);
                    }
                }
                break;
                
            case 'email':
                if (!validateEmail(value)) {
                    setEmailError('ایمیل نامعتبر است');
                    setIsEmailAvailable(false);
                } else {
                    setEmailError('');
                    if (value !== originalEmail) {
                        emailTimer.current = setTimeout(() => {
                            checkAvailability('email', value);
                        }, 500);
                    } else {
                        setIsEmailAvailable(true);
                    }
                }
                break;
                
            case 'phone':
                if (!validatePhone(value)) {
                    setPhoneError('شماره تماس نامعتبر است');
                    setIsPhoneAvailable(false);
                } else {
                    setPhoneError('');
                    if (value !== originalPhone) {
                        phoneTimer.current = setTimeout(() => {
                            checkAvailability('phone', value);
                        }, 500);
                    } else {
                        setIsPhoneAvailable(true);
                    }
                }
                break;
                
            case 'password':
                if (value && !validatePassword(value)) {
                    setPasswordError('رمز عبور باید حداقل 8 کاراکتر و شامل حروف بزرگ، کوچک، عدد و کاراکتر خاص باشد');
                } else {
                    setPasswordError('');
                }
                break;
                
            default:
                break;
        }
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage('');
        
        try {
            const response = await fetch(`${BASE_URL}/users/${id}`, {
                method: 'PUT',
                headers: getAuthHeader(),
                credentials: 'include',
                body: JSON.stringify(formData)
            });
            
            if (!response.ok) {
                if (response.status === 401 || response.status === 403) {
                    localStorage.removeItem('token');
                    navigate('/login');
                    throw new Error('دسترسی غیر مجاز');
                }
                throw new Error('خطا در بروزرسانی اطلاعات کاربر');
            }
            
            setMessage('اطلاعات کاربر با موفقیت بروزرسانی شد');
            setTimeout(() => {
                navigate('/admin/users/list');
            }, 2500);
            
        } catch (error) {
            setMessage(error.message);
        } finally {
            setIsLoading(false);
        }
    };
    
    return (
        <div className="edit-user-container">
            <h2>ویرایش کاربر</h2>
            {message && <div className={`alert ${message.includes('موفقیت') ? 'alert-success' : 'alert-danger'}`}>
                {message}
            </div>}
            
            <form onSubmit={handleSubmit}>
                <div className="form-group">
                    <label htmlFor="username">نام کاربری</label>
                    <input
                        type="text"
                        id="username"
                        name="username"
                        className={`form-control ${usernameError ? 'is-invalid' : ''}`}
                        value={formData.username}
                        onChange={handleChange}
                        required
                    />
                    {usernameError && <div className="invalid-feedback">{usernameError}</div>}
                </div>
                
                <div className="form-group">
                    <label htmlFor="email">ایمیل</label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        className={`form-control ${emailError ? 'is-invalid' : ''}`}
                        value={formData.email}
                        onChange={handleChange}
                        required
                    />
                    {emailError && <div className="invalid-feedback">{emailError}</div>}
                </div>
                
                <div className="form-group">
                    <label htmlFor="phone">شماره تماس</label>
                    <input
                        type="text"
                        id="phone"
                        name="phone"
                        className={`form-control ${phoneError ? 'is-invalid' : ''}`}
                        value={formData.phone}
                        onChange={handleChange}
                        autoComplete="off"
                    />
                    {phoneError && <div className="invalid-feedback">{phoneError}</div>}
                    <small className="form-text text-muted">فرمت صحیح: 09123456789</small>
                </div>                               
                <div className="form-group">
                    <label htmlFor="password">رمز عبور جدید (اختیاری)</label>
                    <input
                        type="password"
                        id="password"
                        name="password"
                        className={`form-control ${passwordError ? 'is-invalid' : ''}`}
                        value={formData.password}
                        onChange={handleChange}
                        autoComplete="new-password"
                    />
                    {passwordError && <div className="invalid-feedback">{passwordError}</div>}
                    <small className="form-text text-muted">
                        در صورت عدم تمایل به تغییر رمز عبور، این فیلد را خالی بگذارید
                    </small>
                </div>
                
                <div className="form-group">
                    <label htmlFor="role">نقش کاربر</label>
                    <select
                        id="role"
                        name="role"
                        className="form-control"
                        value={formData.role}
                        onChange={handleChange}
                    >
                        <option value="user">کاربر عادی</option>
                        <option value="admin">مدیر</option>
                    </select>
                </div>
                
                <div className="form-actions">
                    <button 
                        type="submit" 
                        className="btn btn-primary" 
                        disabled={isLoading || !isUsernameAvailable || !isEmailAvailable || !isPhoneAvailable || 
                                 !!usernameError || !!emailError || !!passwordError || !!phoneError}
                    >
                        {isLoading ? 'در حال پردازش...' : 'بروزرسانی کاربر'}
                    </button>
                    <button 
                        type="button" 
                        className="btn btn-secondary" 
                        onClick={() => navigate('/admin/users')}
                    >
                        انصراف
                    </button>
                </div>
            </form>
        </div>
    );
};

export default EditUser;
